import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCompanySlug,
  searchCompaniesForBookingPortal
} from '../../../client-experience/utils/companyAccess';
import './company-not-found.css';

const SEARCH_DEBOUNCE_MS = 320;
const MIN_SEARCH_LENGTH = 2;

const CompanyNotFoundView = ({ attemptedSlug, technicalError }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const debounceRef = useRef(null);

  const isPlainNotFound = useMemo(() => {
    if (!technicalError) return true;
    return technicalError === 'Company not found';
  }, [technicalError]);

  const runSearch = useCallback(async (raw) => {
    const trimmed = (raw || '').trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      setResults([]);
      setSearchError(null);
      setSearching(false);
      return;
    }

    setSearching(true);
    setSearchError(null);
    try {
      const matches = await searchCompaniesForBookingPortal(trimmed);
      setResults(matches);
    } catch (err) {
      console.error('Company search failed:', err);
      setResults([]);
      setSearchError('Search is temporarily unavailable. Please try again in a moment.');
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    const q = query.trim();
    if (q.length < MIN_SEARCH_LENGTH) {
      setResults([]);
      setSearchError(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      runSearch(query);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  const statusText = useMemo(() => {
    if (searchError) return null;
    const q = query.trim();
    if (q.length > 0 && q.length < MIN_SEARCH_LENGTH) {
      return `Type at least ${MIN_SEARCH_LENGTH} characters to search.`;
    }
    if (searching) return 'Searching…';
    if (q.length >= MIN_SEARCH_LENGTH && results.length === 0 && !searching) {
      return 'No businesses match that search.';
    }
    if (results.length > 0) {
      return `${results.length} result${results.length === 1 ? '' : 's'} — pick one to open their booking page.`;
    }
    return '';
  }, [query, results.length, searching, searchError]);

  return (
    <div className="company-not-found">
      <h2 className="company-not-found__title">We couldn’t find this booking page</h2>
      <p className="company-not-found__body">
        The web address may be mistyped, or the business may have updated their link. Please contact the
        store owner for the correct booking link so you can schedule with them.
      </p>
      {attemptedSlug ? (
        <p className="company-not-found__hint">
          <strong>Link tried:</strong> {attemptedSlug}
        </p>
      ) : null}
      {!isPlainNotFound && technicalError ? (
        <p className="company-not-found__hint" role="status">
          {technicalError}
        </p>
      ) : null}

      <label className="company-not-found__label" htmlFor="company-not-found-search">
        Search by business or store name
      </label>
      <div className="company-not-found__input-wrap">
        <input
          id="company-not-found-search"
          className="company-not-found__input"
          type="search"
          autoComplete="off"
          placeholder="e.g. salon name or city"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {searchError ? (
        <p className="company-not-found__status company-not-found__status--error" role="alert">
          {searchError}
        </p>
      ) : (
        <p className="company-not-found__status">{statusText}</p>
      )}

      {results.length > 0 ? (
        <ul className="services-list company-not-found__results" role="list">
          {results.map((company) => {
            const slug = getCompanySlug(company);
            return (
              <li key={company.id} className="company-not-found__results-item" role="listitem">
                <button
                  type="button"
                  className="company-not-found__result"
                  onClick={() => navigate(`/book/${encodeURIComponent(slug)}`, { replace: true })}
                >
                  <span className="company-not-found__name">{company.name || 'Unnamed business'}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default CompanyNotFoundView;
